import React, { useEffect, useMemo, useRef } from "react";
import {
  Box,
  Chip,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Popper,
} from "@mui/material";
import type { AvailableCommandEntry } from "../../../chat/events";
import { PortalScope } from "@/analytics/PortalScope";

export interface CommandPaletteProps {
  /** When non-null, the palette is open and anchored to this element. */
  anchorEl: HTMLElement | null;
  /** The full list of commands surfaced by the active SSE session. */
  commands: AvailableCommandEntry[];
  /** Text after the leading `/` in the composer — used to filter. */
  filter: string;
  /** Index of the currently highlighted command in the filtered list. */
  highlightIndex: number;
  /** Called when the user clicks a command (mouse path; keyboard path
   *  is handled by the Composer). */
  onSelect: (cmd: AvailableCommandEntry) => void;
  /** Called when the user clicks outside the palette. The Composer is
   *  the source of truth for open-state, so it just resets there. */
  onDismiss: () => void;
}

/** Fuzzy-but-cheap filter: case-insensitive substring across title +
 *  description + namespaced_name. Good enough for the small palette
 *  the agent ever surfaces in practice (usually < 20 entries). */
function filterCommands(
  commands: AvailableCommandEntry[],
  needle: string,
): AvailableCommandEntry[] {
  const q = needle.trim().toLowerCase();
  if (!q) return commands;
  return commands.filter((c) => {
    const hay =
      `${c.title} ${c.description} ${c.namespaced_name}`.toLowerCase();
    return hay.includes(q);
  });
}

export function CommandPalette({
  anchorEl,
  commands,
  filter,
  highlightIndex,
  onSelect,
  onDismiss,
}: CommandPaletteProps) {
  const filtered = useMemo(
    () => filterCommands(commands, filter),
    [commands, filter],
  );

  // Keep the highlighted item visible as the list shrinks/scrolls.
  const listRef = useRef<HTMLUListElement | null>(null);
  useEffect(() => {
    if (!anchorEl) return;
    const node = listRef.current?.querySelector<HTMLLIElement>(
      `[data-cmd-idx="${highlightIndex}"]`,
    );
    node?.scrollIntoView({ block: "nearest" });
  }, [anchorEl, highlightIndex, filter]);

  return (
    <Popper
      open={anchorEl !== null}
      anchorEl={anchorEl}
      placement="top-start"
      modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
      style={{ zIndex: 1300 }}
    >
      <Paper
        elevation={8}
        sx={{
          width: 420,
          maxWidth: "90vw",
          maxHeight: 320,
          overflow: "auto",
          borderRadius: 1.5,
        }}
        onMouseDown={(e) => {
          // Prevent the composer from blurring (which would unmount the
          // popper before onSelect fires) when the user clicks an item.
          e.preventDefault();
        }}
      >
        <PortalScope section="composer-commands">
        {filtered.length === 0 ? (
          <Box
            sx={{
              p: 2,
              textAlign: "center",
              fontSize: 13,
              color: "text.secondary",
            }}
          >
            {commands.length === 0
              ? "No plugin commands available in this session."
              : `No commands match "${filter}"`}
          </Box>
        ) : (
          <List
            ref={listRef as any}
            dense
            disablePadding
            aria-label="plugin commands"
          >
            {filtered.map((cmd, idx) => (
              <ListItemButton
                key={cmd.namespaced_name}
                data-ph="select"
                data-cmd-idx={idx}
                selected={idx === highlightIndex}
                onClick={() => {
                  onSelect(cmd);
                  onDismiss();
                }}
                sx={{ alignItems: "flex-start", py: 1, gap: 1 }}
              >
                <ListItemText
                  primary={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{ fontWeight: 600, fontSize: 14 }}
                      >
                        {cmd.title}
                      </Box>
                      <Chip
                        size="small"
                        label={cmd.namespaced_name}
                        variant="outlined"
                        sx={{ fontFamily: "monospace", fontSize: 11 }}
                      />
                    </Box>
                  }
                  secondary={
                    <Box
                      component="span"
                      sx={{ fontSize: 12, color: "text.secondary" }}
                    >
                      {cmd.description}
                    </Box>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
        </PortalScope>
      </Paper>
    </Popper>
  );
}

/** Helper exposed for tests and the Composer's keyboard handler. */
export { filterCommands };
